import { useCartStore } from "@features/shopping/stores";
import { computed } from "vue";
import { useRoute } from "vue-router";

export function useCart() {
  // Stores & Dependencies
  const cartStore = useCartStore();
  const route = useRoute("/(product)/cart");

  // Computed properties from the store
  const isCartOpen = computed(() => cartStore.isCartOpen);
  const cartItems = computed(() => cartStore.getCarts);
  const cartCount = computed(() => cartStore.cartsCount);
  const cartMemberId = computed(() => cartStore.getMemberId);
  const hasItems = computed(() => cartStore.hasCarts);
  const shippingTotal = computed(() => cartStore.getShippingTotal);
  const subTotal = computed(() => cartStore.getSubTotal);
  const cartId = computed(() => cartStore.getCartId);
  const loading = computed(() => cartStore.loading);
  const error = computed(() => cartStore.error);

  /**
   * Open the cart
   */
  const openCart = () => {
    cartStore.open = true;
  };

  /**
   * Close the cart
   */
  const closeCart = () => {
    cartStore.open = false;
  };

  /**
   * Toggle the cart
   */
  const toggleCart = () => {
    if (route.path === "/cart") return;
    cartStore.open = !cartStore.open;
  };

  /**
   * Add an item to the cart
   */
  const addToCart = async (productSkuId: number, quantity: number, limitAmount: number) => {
    return await cartStore.addItem({ limitAmount, productSkuId, quantity });
  };

  /**
   * Update an item to the cart
   */
  const updateItem = async (productSkuId: number, quantity: number, limitAmount: number) => {
    return await cartStore.addItem({ limitAmount, productSkuId, quantity, update: true });
  };

  /**
   * Remove an item from the cart
   */
  const removeFromCart = async (productSkuId: string) => {
    return await cartStore.removeItem(productSkuId);
  };

  /**
   * Fetch cart total
   */
  const fetchCartTotal = async (countryCode?: string) => {
    return await cartStore.fetchCartTotal(countryCode);
  };

  /**
   * Clear cart items
   */
  const clearCart = () => cartStore.clearCart();

  /**
   * Clear any error messages
   */
  const clearError = () => cartStore.clearError();

  // Methods
  const methods = {
    addToCart,
    clearCart,
    clearError,
    closeCart,
    fetchCartTotal,
    openCart,
    removeFromCart,
    toggleCart,
    updateItem,
  };

  // States
  const states = {
    cartCount,
    cartId,
    cartItems,
    cartMemberId,
    error,
    hasItems,
    isCartOpen,
    loading,
    shippingTotal,
    subTotal,
  };

  return {
    ...methods,
    ...states,
  };
}
